import { forwardRef, type ButtonHTMLAttributes } from "react";

import { cn } from "../lib/cn";

export type ToggleChipTone = "accent" | "ok" | "warn" | "error" | "neutral";
export type ToggleChipSize = "sm" | "md";

export interface ToggleChipProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-pressed" | "onChange"> {
  pressed: boolean;
  onPressedChange: (pressed: boolean) => void;
  tone?: ToggleChipTone;
  size?: ToggleChipSize;
}

const pressedClasses: Record<ToggleChipTone, string> = {
  accent: "border-focus bg-brand-cyan/10 text-interaction-text",
  ok: "border-status-ok/40 bg-status-ok/10 text-status-ok-text",
  warn: "border-status-warn/40 bg-status-warn/10 text-status-warn-text",
  error: "border-status-error/40 bg-status-error/10 text-status-error-text",
  neutral: "border-border bg-surface-elevated text-primary",
};

const sizeClasses: Record<ToggleChipSize, string> = {
  sm: "h-6 px-2.5 text-xs",
  md: "h-7 px-3 text-xs",
};

export const ToggleChip = forwardRef<HTMLButtonElement, ToggleChipProps>(function ToggleChip(
  {
    pressed,
    onPressedChange,
    tone = "accent",
    size = "sm",
    disabled,
    className,
    children,
    type = "button",
    onClick,
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      aria-pressed={pressed}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) onPressedChange(!pressed);
      }}
      className={cn(
        "inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full border font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-1 focus-visible:ring-offset-background",
        "disabled:cursor-not-allowed disabled:opacity-50",
        sizeClasses[size],
        pressed
          ? pressedClasses[tone]
          : "border-border bg-surface text-muted hover:border-border hover:text-secondary",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
});